/**
 * Security lifecycle hooks — wipe the in-memory DEK on signOut and on idle.
 *
 * Phase 4 of feature/offline-encryption. Phases 1–3 minted, imported, and
 * applied the DEK; this module governs WHEN the in-memory key gets dropped:
 *
 *   1. **Amplify signOut event** — every time the user signs out (any code
 *      path: dashboard "Sign out" button, RequireAuth's auth-failure
 *      "Log out", role-selection bail-out), `Hub` fires `signedOut` and we
 *      drop the key immediately. No call site needs to remember to call
 *      `clearDek()` manually — the listener catches them all.
 *
 *   2. **Idle timeout** — after `IDLE_DEK_WIPE_MS` (15 min by default) with
 *      no `mousedown` / `keydown` / `touchstart`, the key is dropped. The
 *      next interaction transparently re-fetches the wrapped DEK from the
 *      server (the Amplify session is still valid; no re-login required).
 *      `mousemove` is intentionally NOT in the activity set — too noisy,
 *      would never let the timer fire on a tab where the cursor is parked.
 *
 * What this does NOT do:
 *   - Wipe the on-disk encrypted IndexedDB. That stays so existing offline
 *     records survive re-login and are decryptable on the next session.
 *   - Force a re-login. Idle wipe is silent; the next read transparently
 *     re-fetches the same wrapped key from the server.
 *   - Defend against a tablet stolen *while powered on, unlocked, signed
 *     in, and active*. The key is in RAM at that point — physical
 *     security (screen lock + full-disk encryption) handles that case.
 *
 * Mounted once at app boot via `App.tsx`. Returns a teardown function so
 * tests / hot-reload can clean up event listeners cleanly.
 */

import { Hub } from "aws-amplify/utils";
import { getCurrentUser } from "aws-amplify/auth"; // still used by installV2MigrationOnSignIn

import { IDLE_DEK_WIPE_MS } from "../constants";
import { logger } from "../utils/logger";
import { clearDek, getDek } from "./crypto";
import { runV2Migration } from "./v2-migration";
import { clearAuthCache, isPlatformAdminSession } from "../components/RequireAuth";
import { db } from "./schema";
import { SoapAPI } from "../services/soap.service";
import { OnboardingAPI } from "../services/onboarding.service";
import { markCheckpointStale } from "../sync/pull-engine";
import { clearCacheClinicId } from "../utils/cache-scope";
import { isTransientAuthSession } from "../utils/transient-auth-session";
import { resetDeviceTrust, whenDeviceTrusted } from "../utils/device-trust";

// Activity events that reset the idle timer. Kept narrow on purpose:
//   - mousedown / keydown / touchstart cover deliberate user action.
//   - mousemove is excluded — it fires constantly and would defeat the
//     timer on any tab where the cursor isn't perfectly still.
//   - focus / blur are excluded — tab switching shouldn't reset the timer.
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart"] as const;

/**
 * Run sign-in work once the device has passed the backend's device gate:
 * before that, every gated call it makes is a device_verification_required
 * 403 (see utils/device-trust). A password-setup session is skipped here, at
 * sign-in, because by the time trust arrives that session has already ended.
 */
function afterDeviceTrusted(work: () => void): void {
  if (isTransientAuthSession()) return;
  void whenDeviceTrusted().then(work);
}

/**
 * Listen on Amplify's auth Hub for the `signedOut` event and wipe the DEK.
 *
 * Returns the unsubscribe handle from `Hub.listen` so callers can detach
 * cleanly (used in tests; the production install keeps it for the lifetime
 * of the app).
 */
export function installAmplifySignOutHook(): () => void {
  return Hub.listen("auth", ({ payload }) => {
    if (payload.event === "signedOut") {
      resetDeviceTrust();
      clearDek();
      clearSwCaches();
      clearAuthCache();
      clearPhiTables();
      logger.warn("dek wiped on signOut");
    }
  });
}

/**
 * Wipe all PHI tables from IndexedDB on signOut so a subsequent user on the
 * same device cannot be served (or fail to decrypt) a previous user's rows.
 * `drugs` is kept — it is public reference data with no PHI.
 * `sync_queue` is cleared — pending mutations from the signed-out session
 * must not be replayed under a different user's credentials.
 *
 * Returns the wipe promise (resolving even on failure — errors are swallowed,
 * as the next login re-pulls anyway). The signOut path fires and forgets, but
 * the clinic switch in ClinicContext MUST await it: it re-hydrates immediately
 * afterwards, and an unawaited wipe would race the incoming rows and delete
 * half of them, leaving the new clinic's cache silently incomplete.
 */
export function clearPhiTables(): Promise<void> {
  // Reset pull-engine checkpoint to 30 days ago so the next login re-fetches
  // recent data without issuing an unbounded since=epoch query (which can
  // timeout on RDS through SSM tunneling). A 30-day window captures all
  // clinically relevant records while keeping the query bounded.
  //
  // markCheckpointStale() prevents any pull cycle that started before this
  // signOut from overwriting the reset checkpoint when it completes.
  markCheckpointStale();
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    localStorage.setItem("kaagazy_last_sync_timestamp", thirtyDaysAgo);
    localStorage.removeItem("kaagazy_last_synced_at");
    // Clear bootstrap flag so the next login re-runs bootstrap from scratch
    // rather than delta-pulling from the 30-day watermark against an empty IDB.
    localStorage.removeItem("kaagazy_bootstrap_completed_at");
  } catch { /* non-fatal */ }
  // Drop the cache-scope marker with the data it describes, so "no marker"
  // means "no clinic data here" rather than a stale claim about an empty DB.
  clearCacheClinicId();

  return db.transaction("rw",
    [db.patients, db.visits, db.vitals, db.prescriptions,
     db.appointments, db.allergies, db.sync_queue, db.users, db.clinics,
     db.device_settings, db.crypto_keys, db.treatment_plan_items],
    () => Promise.all([
      db.patients.clear(),
      db.visits.clear(),
      db.vitals.clear(),
      db.prescriptions.clear(),
      db.appointments.clear(),
      db.allergies.clear(),
      db.sync_queue.clear(),
      db.users.clear(),
      db.clinics.clear(),
      db.device_settings.clear(),
      db.crypto_keys.clear(),
      db.treatment_plan_items.clear(),
    ])
  )
    .then(() => undefined)
    .catch(() => { /* non-fatal — next login will re-pull fresh data */ });
}

/**
 * Tell the service worker to wipe runtime and drugs caches on logout so a
 * subsequent user on the same device cannot be served responses from a
 * previous session. The app shell cache (JS/CSS/HTML) is kept — it holds
 * no user data. No-ops silently if the SW is unavailable.
 */
function clearSwCaches(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((reg) => reg.active?.postMessage({ type: "CLEAR_CACHES" }))
    .catch(() => { /* non-fatal — SW may not be active in dev */ });
}

/**
 * Wipe the in-memory DEK after `thresholdMs` of inactivity. Resets on every
 * deliberate user gesture (`mousedown` / `keydown` / `touchstart`).
 *
 * Returns a teardown function that clears the pending timer and removes
 * the activity listeners. Idempotent: calling teardown twice is safe.
 */
export function installIdleWipe(thresholdMs: number = IDLE_DEK_WIPE_MS): () => void {
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let onlineWipeHandler: (() => void) | null = null;

  const doWipe = () => {
    clearDek();
    logger.warn("dek wiped on idle timeout");
  };

  const reset = () => {
    if (idleTimer !== null) clearTimeout(idleTimer);
    // Cancel any deferred online-wipe from a prior offline-idle window.
    if (onlineWipeHandler) {
      window.removeEventListener("online", onlineWipeHandler);
      onlineWipeHandler = null;
    }
    idleTimer = setTimeout(() => {
      idleTimer = null;
      if (!navigator.onLine) {
        // Offline — cannot re-fetch the DEK after wiping, so wiping now would
        // make all cached data permanently unreadable until the backend is
        // reachable again. Defer: wipe the moment the device comes back online
        // (at which point getDek() can transparently re-fetch the key).
        // Cap the deferral at 30 min so the 15-min idle security guarantee is
        // not violated for arbitrarily long offline sessions.
        const MAX_DEFER_MS = 30 * 60 * 1000;
        let deferTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
          deferTimer = null;
          if (onlineWipeHandler) {
            window.removeEventListener("online", onlineWipeHandler);
            onlineWipeHandler = null;
          }
          doWipe();
        }, MAX_DEFER_MS);

        onlineWipeHandler = () => {
          onlineWipeHandler = null;
          if (deferTimer !== null) { clearTimeout(deferTimer); deferTimer = null; }
          doWipe();
        };
        window.addEventListener("online", onlineWipeHandler, { once: true });
        logger.warn("dek wipe deferred — offline at idle timeout");
        return;
      }
      doWipe();
    }, thresholdMs);
  };

  // Start the clock immediately so a tab that stays untouched from the
  // moment of installation eventually wipes.
  reset();

  for (const evt of ACTIVITY_EVENTS) {
    window.addEventListener(evt, reset, { passive: true });
  }

  return () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    if (onlineWipeHandler) {
      window.removeEventListener("online", onlineWipeHandler);
      onlineWipeHandler = null;
    }
    for (const evt of ACTIVITY_EVENTS) {
      window.removeEventListener(evt, reset);
    }
  };
}

/**
 * Listen for Amplify `signedIn` events and trigger the v1 → v2 encryption
 * migration. Also probes the current auth state at install time so a
 * cold-boot into an existing session (returning user with a valid token)
 * triggers a migration pass.
 *
 * `runV2Migration` is idempotent and short-circuits via a localStorage flag
 * once it has completed cleanly, so re-triggering on every signIn is safe.
 *
 * Errors are logged but never thrown — a failed migration leaves v1 rows
 * in place; the encryption-bridge unwraps tolerate them and the next
 * trigger picks up the leftovers.
 */
export function installV2MigrationOnSignIn(): () => void {
  const trigger = () => {
    // A password-setup session is signed out again inside the same handler.
    // Working against it only produces device_verification_required 403s that
    // race the sign-out; a real login fires `signedIn` again.
    if (isTransientAuthSession()) return;
    runV2Migration().catch(err => {
      const name = err instanceof Error ? err.name : "Unknown";
      logger.warn("v2-migration trigger failed", { error_name: name });
    });
  };

  // Cold-boot probe: if the user is already authenticated when the app
  // loads, the Hub `signedIn` event has already fired. Check current auth
  // state directly so we don't miss the migration on a returning visit.
  getCurrentUser()
    .then(() => trigger())
    .catch(() => { /* not authenticated yet — wait for signedIn event */ });

  return Hub.listen("auth", ({ payload }) => {
    if (payload.event === "signedIn") afterDeviceTrusted(trigger);
  });
}

/**
 * Eagerly warm the DEK cache on app boot and on every signIn so it is in
 * memory before the user navigates offline. The DEK fetch is a no-op when
 * `dekKey` is already set (see getDek() in crypto.ts). Errors are silently
 * swallowed here — the crypto layer will surface them on the first actual
 * encrypt/decrypt call.
 */
export function installEagerDekLoad(): () => void {
  const warm = (triggerPull = false) => {
    // See the note in installV2MigrationOnSignIn: a setup-only session is gone
    // before the DEK fetch can land.
    if (isTransientAuthSession()) return;
    getDek()
      .then(() => {
        // Re-trigger a pull cycle so Dexie gets populated if the startup pull
        // ran before the DEK was ready. Only on explicit boot/signIn — NOT
        // from the online listener path (would create an infinite loop).
        if (triggerPull) window.dispatchEvent(new Event("online"));
      })
      .catch(err => {
        const name = err instanceof Error ? err.name : "Unknown";
        logger.warn("eager DEK pre-load failed", { error_name: name });
      });
  };

  // Returning session: Amplify restores the token asynchronously. Wait until
  // getCurrentUser() confirms auth is ready before attempting the DEK fetch.
  getCurrentUser().then(() => warm(true)).catch(() => { /* not yet authed */ });

  // Coming back online after offline boot: re-warm but don't fire another
  // synthetic online event (we're already handling a real one).
  const onlineWarm = () => warm(false);
  window.addEventListener("online", onlineWarm);

  // Re-warm on every signIn (fresh login or after idle wipe), once the device
  // is trusted. The cold-boot and online paths above stay ungated: they must
  // work offline, where no gated request can succeed.
  const stopHub = Hub.listen("auth", ({ payload }) => {
    if (payload.event === "signedIn") afterDeviceTrusted(() => warm(true));
  });

  return () => {
    stopHub();
    window.removeEventListener("online", onlineWarm);
  };
}

/**
 * Cache the current user's info and clinic data in IndexedDB on every signIn
 * (and on cold boot if already authenticated). This lets ContinueDiagnosis and
 * other pages that need doctor name / clinic name work fully offline.
 *
 * Data is cleared on signOut via clearPhiTables() above so a subsequent user
 * on the same device doesn't see stale provider info.
 */
export function installReferenceDataCache(): () => void {
  const cache = () => {
    // See the note in installV2MigrationOnSignIn: a setup-only session is gone
    // before these fetches can land.
    if (isTransientAuthSession()) return;
    // A PlatformAdmin has no clinic to cache, and both reads would 403/404.
    if (isPlatformAdminSession()) return;
    getCurrentUser()
      .then(({ userId }) =>
        Promise.all([
          SoapAPI.getUser(userId)
            .then(u => db.users.put(u))
            .catch(err => {
              const name = err instanceof Error ? err.name : "Unknown";
              logger.warn("reference-data cache: user fetch failed", { error_name: name });
            }),
          OnboardingAPI.getClinic()
            .then(c => db.clinics.put(c))
            .catch(err => {
              const name = err instanceof Error ? err.name : "Unknown";
              logger.warn("reference-data cache: clinic fetch failed", { error_name: name });
            }),
        ])
      )
      .catch(() => { /* not authenticated yet */ });
  };

  // Cold-boot: may already be authenticated.
  cache();

  // After a new-device OTP this is what fills the offline copy, so it must
  // wait for trust rather than 403 at sign-in and never run again.
  return Hub.listen("auth", ({ payload }) => {
    if (payload.event === "signedIn") afterDeviceTrusted(cache);
  });
}

/**
 * Install all security-lifecycle hooks at app boot. Returns a combined
 * teardown that detaches the Hub listeners and the idle-wipe timer.
 *
 * Mounted from `App.tsx`. Designed to live for the entire app lifetime;
 * the teardown exists for tests and React StrictMode double-invocation
 * cleanup.
 */
export function installSecurityLifecycle(): () => void {
  const stopSignOut  = installAmplifySignOutHook();
  const stopMigrate  = installV2MigrationOnSignIn();
  const stopIdle     = installIdleWipe();
  const stopDekWarm  = installEagerDekLoad();
  const stopRefCache = installReferenceDataCache();
  return () => {
    stopSignOut();
    stopMigrate();
    stopIdle();
    stopDekWarm();
    stopRefCache();
  };
}